import React, { useState } from 'react';
import { Spinner } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faComments,
  faUser,
  faTrash,
  faChevronDown,
  faChevronUp,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import BZAPIService from '../../services/BZAPIService';
import PropTypes from 'prop-types';
import moment from 'moment';

const Comments = ({
  projectId,
  versionId,
  productName,
  productId,
  fetchUserVersionState,
  selectedVersion,
  userVersionData
}) => {
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showComments, setShowComments] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleChange = event => {
    setComment(event.target.value);
  };

  const handleAddComment = async () => {
    if (!comment) return;
    setIsLoading(true);
    await BZAPIService.bsaCreateVersionComment({
      projectId,
      versionId,
      text: comment,
      productName,
      productId
    });
    await fetchUserVersionState(selectedVersion);
    setComment('');
    setIsLoading(false);
  };

  const handleDeleteComment = async commentId => {
    setIsDeleting(commentId);
    await BZAPIService.bsaDeleteVersionComment({
      projectVersionId: userVersionData.id,
      commentId
    });
    await fetchUserVersionState(selectedVersion);
    setIsDeleting(false);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const commentCount = userVersionData?.comments?.length || 0;

  return (
    <div className="bst-comments-section">
      {/* Header */}
      <div className="bst-comments-header" onClick={() => setShowComments(!showComments)}>
        <div className="bst-comments-title">
          <span className="bst-comments-icon">
            <FontAwesomeIcon icon={faComments} />
          </span>
          <span className="bst-comments-label">Version Comments</span>
          <span className="bst-comments-count">{commentCount}</span>
        </div>
        <button className="bst-comments-toggle">
          {showComments ? 'Hide' : 'Show'}
          <FontAwesomeIcon icon={showComments ? faChevronUp : faChevronDown} className="ms-2" />
        </button>
      </div>

      {/* Comments Content */}
      {showComments && (
        <div className="bst-comments-content">
          {/* Comment List */}
          {commentCount > 0 && (
            <div className="bst-comments-list">
              {userVersionData?.comments.map((commentItem, index) => (
                <div key={index} className="bst-comment-item">
                  <div className="bst-comment-avatar">
                    <FontAwesomeIcon icon={faUser} />
                  </div>
                  <div className="bst-comment-body">
                    <div className="bst-comment-meta">
                      <span className="bst-comment-author">{commentItem.userId}</span>
                      <span className="bst-comment-time">{moment.utc(commentItem.timestamp).fromNow()}</span>
                    </div>
                    <div className="bst-comment-text">{commentItem.text}</div>
                  </div>
                  <button
                    className="bst-comment-delete"
                    onClick={() => handleDeleteComment(commentItem.id)}
                    disabled={isDeleting === commentItem.id}
                  >
                    {isDeleting === commentItem.id ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {commentCount === 0 && (
            <div className="bst-comments-empty">
              <FontAwesomeIcon icon={faComments} className="empty-icon" />
              <p>No comments yet. Be the first to add one!</p>
            </div>
          )}

          {/* Add Comment Form */}
          <div className="bst-comment-form">
            <textarea
              className="bst-comment-input"
              rows="2"
              placeholder="Write a comment... (Press Enter to submit)"
              value={comment}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
            <button className="bst-comment-submit" disabled={!comment || isLoading} onClick={handleAddComment}>
              {isLoading ? <Spinner size="sm" /> : <FontAwesomeIcon icon={faPaperPlane} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

Comments.propTypes = {
  projectId: PropTypes.string.isRequired,
  versionId: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  productId: PropTypes.string.isRequired,
  fetchUserVersionState: PropTypes.func.isRequired,
  selectedVersion: PropTypes.object,
  comments: PropTypes.arrayOf(PropTypes.string),
  setUserVersionData: PropTypes.func.isRequired,
  userVersionData: PropTypes.object
};

export default Comments;
